import calculateExtendedPrice from '../calculate-extended-price';
import roundPrice from '../round-price';

/**
 * Rounded extended line price from quantity, unit price, and rules; freight total; line total;
 * and tax on that total from a single combined rate.
 *
 * **Tax:** `taxRate === 0` means the line is non-taxable (no tax dollars applied).
 *
 * **Freight:** `freightCharge === 0` means freight is not present on the line, or the freight quantity
 * type is flat so there is no charge multiplier to apply here (`freightTotal` will be zero).
 *
 * @param {object} params
 * @param {number} params.quantity
 * @param {number} params.unitPrice
 * @param {Array} [params.rules=[]]
 * @param {number} [params.decimals=2]
 * @param {number} [params.taxRate=0] - Total tax rate (e.g. `0.08` for 8%). Use `0` when the line is non-taxable.
 * @param {number} [params.freightCharge=0] - Freight charge multiplier (e.g. per unit). Use `0` when there is no freight or quantity type is flat.
 * @param {number} [params.freightQuantity=0]
 * @returns {{ materialPrice: number, freightTotal: number, totalPrice: number, taxTotal: number }}
 */
export default function calculateDisplayPriceAndTaxes({
  quantity,
  unitPrice,
  rules = [],
  decimals = 2,
  taxRate = 0,
  freightCharge = 0,
  freightQuantity = 0,
}) {
  const materialPriceRaw = calculateExtendedPrice({ quantity, unitPrice, rules });
  const freightTotalRaw = freightCharge * freightQuantity;
  const subTotalPriceRaw = materialPriceRaw + freightTotalRaw;
  const taxTotalRaw = subTotalPriceRaw * taxRate;
  const totalPriceRaw = subTotalPriceRaw + taxTotalRaw;

  return {
    materialPrice: roundPrice(materialPriceRaw, decimals),
    freightTotal: roundPrice(freightTotalRaw, decimals),
    subTotalPrice: roundPrice(subTotalPriceRaw, decimals),
    taxTotal: roundPrice(taxTotalRaw, decimals),
    totalPrice: roundPrice(totalPriceRaw, decimals),
  };
}
